import { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { Modal } from "@/components/Modals/Modal";

import { MappingFieldOption } from "../../utils/mappingFieldConfigs";

import { FoundFieldMapping, matchHeaderIndex, ParsedXlsxData } from "./titleGate.utils";

const MAX_PREVIEW_ROWS = 50;

interface MappedPreviewModalProps {
	isOpened: boolean;
	handleClose: () => void;
	parsedData?: ParsedXlsxData;
	foundFields?: FoundFieldMapping[];
	options: MappingFieldOption[];
}

interface PreviewColumn {
	label: string;
	columnIndex: number;
}

/**
 * PREC-3536 Phase-4 preview modal: renders the mapped contacts (e.g. Full Name / Job Title /
 * Company) from the client-side parse, one column per mapped field.
 */
export const MappedPreviewModal: FunctionComponent<MappedPreviewModalProps> = ({
	isOpened,
	handleClose,
	parsedData,
	foundFields,
	options,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`csvEnrichment.titleGate.${key}`);

	const columns = useMemo((): PreviewColumn[] => {
		if (!parsedData) {
			return [];
		}

		return (foundFields ?? [])
			.filter((field) => !!field.assignedField)
			.map((field) => ({
				label:
					field.assignedField === "customField"
						? field.foundField
						: (options.find((option) => option.id === field.assignedField)?.title ??
							field.foundField),
				columnIndex: matchHeaderIndex(parsedData.headers, field.foundField),
			}))
			.filter((column) => column.columnIndex >= 0);
	}, [parsedData, foundFields, options]);

	const rows = useMemo(() => (parsedData?.rows ?? []).slice(0, MAX_PREVIEW_ROWS), [parsedData]);

	if (!isOpened) {
		return null;
	}

	const totalRows = parsedData?.rows.length ?? 0;
	const rowCounts = { shown: rows.length, total: totalRows };

	return (
		<Modal
			footer={
				<>
					<span className="text-xs text-text-meta">
						{t("csvEnrichment.titleGate.previewFootNote", rowCounts)}
					</span>
					<Button title={ts("previewConfirm")} onClick={handleClose} />
				</>
			}
			handleClose={handleClose}
			isOpened={isOpened}
			size="lg"
			subtitle={t("csvEnrichment.titleGate.previewSubtitle", rowCounts)}
			title={ts("previewTitle")}
		>
			<div className="max-h-[56vh] overflow-auto rounded-xl border border-gray-200">
				<table className="w-full text-sm" data-test="mapped-preview-table">
					<thead className="sticky top-0 bg-gray-100">
						<tr>
							{columns.map((column) => (
								<th
									key={column.label}
									className="whitespace-nowrap px-4 py-2 text-left text-xs font-semibold text-text-meta"
								>
									{column.label}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{rows.map((row, rowIndex) => (
							<tr key={`preview-row-${rowIndex}`} className="border-t border-gray-200">
								{columns.map((column) => (
									<td
										key={`${column.label}-${rowIndex}`}
										className="max-w-[240px] overflow-hidden text-ellipsis whitespace-nowrap px-4 py-2 text-text"
									>
										{row[column.columnIndex] ?? ""}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</Modal>
	);
};
